import { ApiClientError, isRecord, requestJson } from "./http.js";

/** Auto-lock caps the server accepts. */
export const AUTO_LOCK_CAPS: ReadonlyArray<number> = [5, 10, 15, 30, 60, 240];
export const POLICY_MIN_PASSWORD_FLOOR = 12;
export const POLICY_MIN_PASSWORD_CEILING = 64;
/** Remember periods (days) the server accepts for remembered installations. */
export const REMEMBER_DEVICE_DAYS: ReadonlyArray<number> = [7, 14, 30, 60, 90];

/** Instance-wide rules set by an admin. */
export interface Policies {
  readonly requireTwoFactor: boolean;
  readonly minPasswordLength: number;
  readonly maxAutoLockMinutes: number | null;
  readonly exportsDisabled: boolean;
  /** Every account is enrolled in admin-assisted recovery while an instance recovery key exists. */
  readonly requireAdminRecovery: boolean;
  /** A new device needs the master password plus a passkey or the approval of a trusted device. */
  readonly requireTrustedDevices: boolean;
  /** A new sign-in ends every other session of the account. */
  readonly singleActiveSession: boolean;
  /** Phishing-resistant 2FA: a passkey is required and replaces codes at sign-in. */
  readonly requirePasskey: boolean;
  /** Locking a client (extension) ends its session too, so every unlock is a full sign-in. */
  readonly signOutOnLock: boolean;
  /** A remembered installation (extension, later desktop) may sign in with its device key instead of the second factor. */
  readonly rememberDevices: boolean;
  /** How long a remembered installation stands in for the second factor. */
  readonly rememberDevicesDays: number;
  /** The extension may sign in through this web app with a passkey (link handoff). */
  readonly allowExtensionLink: boolean;
  /** The extension may act as a passkey provider for websites (off until roadmap step 35). */
  readonly passkeyProvider: boolean;
}

export interface AdminPolicies extends Policies {
  readonly updatedAt: number | null;
  readonly updatedByEmail: string | null;
}

export const DEFAULT_POLICIES: Policies = {
  requireTwoFactor: false,
  minPasswordLength: POLICY_MIN_PASSWORD_FLOOR,
  maxAutoLockMinutes: null,
  exportsDisabled: false,
  requireAdminRecovery: false,
  requireTrustedDevices: false,
  singleActiveSession: false,
  requirePasskey: false,
  signOutOnLock: false,
  rememberDevices: true,
  rememberDevicesDays: 30,
  allowExtensionLink: true,
  passkeyProvider: false
};

export function isPolicies(value: unknown): value is Policies {
  return isRecord(value) &&
    typeof value["requireTwoFactor"] === "boolean" &&
    typeof value["minPasswordLength"] === "number" &&
    (value["maxAutoLockMinutes"] === null || typeof value["maxAutoLockMinutes"] === "number") &&
    typeof value["exportsDisabled"] === "boolean" &&
    typeof value["requireAdminRecovery"] === "boolean" &&
    typeof value["requireTrustedDevices"] === "boolean" &&
    typeof value["singleActiveSession"] === "boolean" &&
    typeof value["requirePasskey"] === "boolean" &&
    typeof value["signOutOnLock"] === "boolean" &&
    typeof value["rememberDevices"] === "boolean" &&
    typeof value["rememberDevicesDays"] === "number" &&
    typeof value["allowExtensionLink"] === "boolean" &&
    typeof value["passkeyProvider"] === "boolean";
}

function isAdminPolicies(value: unknown): value is AdminPolicies {
  return isPolicies(value) &&
    isRecord(value) &&
    (value["updatedAt"] === null || typeof value["updatedAt"] === "number") &&
    (value["updatedByEmail"] === null || typeof value["updatedByEmail"] === "string");
}

function policiesFrom(body: unknown): AdminPolicies {
  if (!isRecord(body) || !isAdminPolicies(body["policies"])) {
    throw new ApiClientError(500, "Unerwartete Antwort vom Server.");
  }
  return body["policies"];
}

export async function fetchAdminPolicies(): Promise<AdminPolicies> {
  return policiesFrom(await requestJson("/api/v1/admin/policies"));
}

export async function saveAdminPolicies(policies: Policies): Promise<AdminPolicies> {
  const body: Policies = {
    requireTwoFactor: policies.requireTwoFactor,
    minPasswordLength: policies.minPasswordLength,
    maxAutoLockMinutes: policies.maxAutoLockMinutes,
    exportsDisabled: policies.exportsDisabled,
    requireAdminRecovery: policies.requireAdminRecovery,
    requireTrustedDevices: policies.requireTrustedDevices,
    singleActiveSession: policies.singleActiveSession,
    requirePasskey: policies.requirePasskey,
    signOutOnLock: policies.signOutOnLock,
    rememberDevices: policies.rememberDevices,
    rememberDevicesDays: policies.rememberDevicesDays,
    allowExtensionLink: policies.allowExtensionLink,
    passkeyProvider: policies.passkeyProvider
  };
  return policiesFrom(await requestJson("/api/v1/admin/policies", { method: "PUT", body }));
}

/** Asks the server to clear a vault export: master password proof and policy check, recorded in the audit log. */
export async function requestExport(currentAuthKeyB64: string): Promise<void> {
  const body = await requestJson("/api/v1/account/export", { method: "POST", body: { currentAuthKeyB64 } });
  if (!isRecord(body) || body["ok"] !== true) {
    throw new ApiClientError(500, "Unerwartete Antwort vom Server.");
  }
}

/** The longest auto-lock allowed: the user's choice, capped by the policy. */
export function cappedAutoLock(minutes: number, cap: number | null): number {
  return cap === null ? minutes : Math.min(minutes, cap);
}

/** Lock options at or under the cap; the cap itself when none fits. */
export function allowedLockOptions<T extends number>(options: ReadonlyArray<T>, cap: number | null): ReadonlyArray<number> {
  if (cap === null) {
    return options;
  }
  const allowed: number[] = options.filter((n: T): boolean => n <= cap);
  return allowed.length === 0 ? [cap] : allowed;
}
